import React, { useState, useEffect } from 'react';
import { Link, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { Bot, FileText, FolderOpen, Tag, LogOut, Menu, X, AlertCircle, Loader } from 'lucide-react';
import { useAuthStore } from '../../store/authStore';

export default function PromptAdminLayout() {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, loading: authLoading, error: authError, signOut } = useAuthStore();
  const [sidebarOpen, setSidebarOpen] = useState(false);

  useEffect(() => {
    // Redirect to login if not authenticated
    if (!authLoading && !user && location.pathname !== '/prompt-admin/login') {
      navigate('/prompt-admin/login');
    }
  }, [user, authLoading, navigate, location.pathname]);

  const handleSignOut = async () => {
    await signOut();
    navigate('/prompt-admin/login');
  };

  if (authLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-100">
        <div className="text-center">
          <Loader className="h-8 w-8 text-[#F98B3D] animate-spin mx-auto mb-4" />
          <p className="text-gray-600">Loading admin panel...</p>
        </div>
      </div>
    );
  }

  if (authError) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-100">
        <div className="bg-red-50 border-l-4 border-red-400 p-4 rounded-md max-w-lg">
          <div className="flex">
            <AlertCircle className="h-5 w-5 text-red-400" />
            <div className="ml-3">
              <h3 className="text-sm font-medium text-red-800">Authentication Error</h3>
              <div className="mt-2 text-sm text-red-700">
                <p>{authError}</p>
                <button
                  onClick={() => navigate('/prompt-admin/login')}
                  className="mt-3 inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-red-600 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
                >
                  Return to Login
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!user) {
    // If we're on the login page, render the Outlet (which will be the login component)
    if (location.pathname === '/prompt-admin/login') {
      return <Outlet />;
    }
    // Otherwise, return null while redirecting
    return null; 
  }

  return (
    <div className="min-h-screen bg-gray-100">
      {/* Mobile sidebar */}
      <div className="lg:hidden">
        <div className={`fixed inset-0 bg-gray-600 bg-opacity-75 z-40 transition-opacity ease-linear duration-300 ${
          sidebarOpen ? 'opacity-100' : 'opacity-0 pointer-events-none'
        }`} onClick={() => setSidebarOpen(false)} />

        <div className={`fixed inset-y-0 left-0 flex flex-col z-40 w-64 bg-white transform ease-in-out duration-300 ${
          sidebarOpen ? 'translate-x-0' : '-translate-x-full'
        }`}>
          <div className="h-16 flex items-center justify-between px-4 border-b border-gray-200">
            <Link to="/prompt-admin" className="flex items-center">
              <Bot className="h-8 w-8 text-[#F98B3D]" />
              <span className="ml-2 text-xl font-semibold text-gray-900">Prompt Admin</span>
            </Link>
            <button onClick={() => setSidebarOpen(false)} className="text-gray-500 hover:text-gray-600">
              <X className="h-6 w-6" />
            </button>
          </div>
          <div className="flex-1 overflow-y-auto">
            <nav className="px-2 py-4 space-y-1">
              <Link
                to="/prompt-admin/prompts"
                className={`flex items-center px-4 py-2 text-sm font-medium rounded-md ${
                  location.pathname.includes('/prompts')
                    ? 'bg-gray-100 text-gray-900'
                    : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                }`}
              >
                <FileText className="h-5 w-5 mr-3" />
                Prompts
              </Link>
              <Link
                to="/prompt-admin/categories"
                className={`flex items-center px-4 py-2 text-sm font-medium rounded-md ${
                  location.pathname.includes('/categories')
                    ? 'bg-gray-100 text-gray-900'
                    : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                }`}
              >
                <FolderOpen className="h-5 w-5 mr-3" />
                Categories
              </Link>
              <Link
                to="/prompt-admin/tags"
                className={`flex items-center px-4 py-2 text-sm font-medium rounded-md ${
                  location.pathname.includes('/tags')
                    ? 'bg-gray-100 text-gray-900'
                    : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                }`}
              >
                <Tag className="h-5 w-5 mr-3" />
                Tags
              </Link>
            </nav>
          </div>
          <div className="p-4 border-t border-gray-200">
            <button
              onClick={handleSignOut}
              className="flex items-center w-full px-4 py-2 text-sm font-medium text-gray-600 rounded-md hover:bg-gray-50 hover:text-gray-900"
            >
              <LogOut className="h-5 w-5 mr-3" />
              Sign Out
            </button>
          </div>
        </div>
      </div>

      {/* Desktop sidebar */}
      <div className="hidden lg:flex lg:flex-col lg:w-64 lg:fixed lg:inset-y-0 lg:border-r lg:border-gray-200 lg:bg-white">
        <div className="h-16 flex items-center justify-between px-6 border-b border-gray-200">
          <Link to="/prompt-admin" className="flex items-center">
            <Bot className="h-8 w-8 text-[#F98B3D]" />
            <span className="ml-2 text-xl font-semibold text-gray-900">Prompt Admin</span>
          </Link>
        </div>
        <div className="flex-1 overflow-y-auto">
          <nav className="px-4 py-6 space-y-1">
            <Link
              to="/prompt-admin/prompts"
              className={`flex items-center px-4 py-2 text-sm font-medium rounded-md ${
                location.pathname.includes('/prompts')
                  ? 'bg-gray-100 text-gray-900'
                  : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
              }`}
            >
              <FileText className="h-5 w-5 mr-3" />
              Prompts
            </Link>
            <Link
              to="/prompt-admin/categories"
              className={`flex items-center px-4 py-2 text-sm font-medium rounded-md ${
                location.pathname.includes('/categories')
                  ? 'bg-gray-100 text-gray-900'
                  : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
              }`}
            >
              <FolderOpen className="h-5 w-5 mr-3" />
              Categories
            </Link>
            <Link
              to="/prompt-admin/tags"
              className={`flex items-center px-4 py-2 text-sm font-medium rounded-md ${
                location.pathname.includes('/tags')
                  ? 'bg-gray-100 text-gray-900'
                  : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
              }`}
            >
              <Tag className="h-5 w-5 mr-3" />
              Tags
            </Link>
          </nav>
        </div>
        <div className="p-4 border-t border-gray-200">
          <button
            onClick={handleSignOut}
            className="flex items-center w-full px-4 py-2 text-sm font-medium text-gray-600 rounded-md hover:bg-gray-50 hover:text-gray-900"
          >
            <LogOut className="h-5 w-5 mr-3" />
            Sign Out
          </button>
        </div>
      </div>

      {/* Main content */}
      <div className="lg:pl-64 flex flex-col flex-1">
        <div className="sticky top-0 z-10 flex-shrink-0 h-16 bg-white border-b border-gray-200 lg:hidden">
          <div className="flex items-center justify-between px-4 h-full">
            <Link to="/prompt-admin" className="flex items-center">
              <Bot className="h-8 w-8 text-[#F98B3D]" />
              <span className="ml-2 text-xl font-semibold text-gray-900">Prompt Admin</span>
            </Link>
            <button
              onClick={() => setSidebarOpen(true)}
              className="text-gray-500 hover:text-gray-600 focus:outline-none"
            >
              <Menu className="h-6 w-6" />
            </button>
          </div>
        </div>

        <main className="flex-1 pb-8">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
            <Outlet />
          </div>
        </main>
      </div>
    </div>
  );
}